import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ExamType, OSCECategory } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';

@Entity()
export class OSCEGroup extends MongoEntity {
  static override _class = Names.OSCEGroup._class;
  static override _collectionName = Names.OSCEGroup._collectionName;

  @validate(nonOptional())
  @Field(type => String)
  name: Maybe<string>;

  @Field(type => [String], { default: v => v ?? [] })
  osceTypes: Maybe<OSCECategory[]>;

  @Field(type => String)
  examType: Maybe<ExamType>;

  @Field(type => Boolean, { default: v => v ?? false })
  archived: Maybe<boolean>;
}

export const getGroupsFromOsceTesterTuples = (
  osceTesterTuples: { osceID: string; osceGroupID: Maybe<string> }[],
): Array<{ osceGroupID?: string; osceIDs: string[] }> => {
  return osceTesterTuples.reduce<Array<{ osceGroupID?: string; osceIDs: string[] }>>((acc, curr) => {
    if (!curr.osceGroupID) acc.push({ osceIDs: [curr.osceID] });
    else {
      const existing = acc.find(group => group.osceGroupID === curr.osceGroupID);
      if (existing) existing.osceIDs.push(curr.osceID);
      else acc.push({ osceGroupID: curr.osceGroupID, osceIDs: [curr.osceID] });
    }

    return acc;
  }, []);
};

export const getGroupsFromItemsAndTesterTuples = <T extends { osceID: string }>(
  items: T[],
  osceTesterTuples: { osceID: string; osceGroupID: Maybe<string> }[],
): Array<{ osceGroupID?: string; items: T[] }> => {
  const osceGroups = getGroupsFromOsceTesterTuples(osceTesterTuples) as Array<{
    osceGroupID?: string;
    osceIDs: string[];
    items: T[];
  }>;

  // make sure we're not manipulating the original slot array
  const copiedItems = [...items];

  for (const osceGroup of osceGroups) {
    osceGroup.items = copiedItems.filter(slot => osceGroup.osceIDs.includes(slot.osceID));
    for (const osceGroupSlot of osceGroup.items) {
      const index = copiedItems.indexOf(osceGroupSlot);
      if (index >= 0) copiedItems.splice(index, 1);
    }
  }

  for (const remainingSlot of copiedItems) {
    osceGroups.push({ items: [remainingSlot], osceIDs: [remainingSlot.osceID] });
  }

  return osceGroups;
};
